import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'

import DashboardHubTopicsCard from '../DashboardHubTopicsCard/DashboardHubTopicsCard'
import Swiper from '../../../../components/Swiper/Swiper'
import ArticleCard from '../../../../components/ArticleCard/ArticleCard'
import LoadingIndicator from '../../../../components/LoadingIndicator/LoadingIndicator'

import { getCssVar } from '../../../../utils/css'
import { camelCase } from '../../../../utils/strings'
import { graphqlClient, gql } from '../../../../utils/gql'

export default function DashboardHubLanding (props = {
    searchPathname: null,
    sources: null,
    topics: null
}) {
    const {
        searchPathname,
        topics
    } = props

    const history = useHistory()

    const { location } = history

    const [featured, setFeatured] = useState(
        (history.location.state || {}).featured
            || null)

    const [recommended, setRecommended] = useState(
        (history.location.state || {}).recommended
            || null)

    const [latest, setLatest] = useState(
        (history.location.state || {}).latest
            || null)

    const [exclusive, setExclusive] = useState(
        (history.location.state || {}).exclusive
            || null)

    const [topicArticles, setTopicArticles] = useState(
        (history.location.state || {}).topicArticles
            || null)

    useEffect(async () => {
        const queryArticlesReturn = gql`
            id
            title
            slug
            created_at
            durations
            thumbnail
            tags {
                id
                slug
                title
            }
            source {
                id
                slug
                title
            }
            recommended_roles
        `

        const topicsGql = topics
            .map(obj => gql`
                ${camelCase(obj.slug)}: articles (
                    where: {
                        tags: { slug: { _eq: "${obj.slug}" } }
                    },
                    limit: 11,
                    order_by: [
                        { created_at: desc }
                    ]
                ) {
                    ${queryArticlesReturn}
                }
            `)
            .join('\n')

        const { latest, featured, ...topicsObj } = await graphqlClient
            .request(gql`
                query {
                    latest: articles (
                        limit: 12,
                        order_by: [
                            { source_created_at: desc },
                            { created_at: desc }
                        ]
                    ) {
                        ${queryArticlesReturn}
                    }

                    featured: articles (
                        where: {
                            _and: [
                                { thumbnail: { _is_null: false } }
                            ]
                        },
                        limit: 2,
                        order_by: [
                            { recommended_roles: asc },
                            { source_id: desc },
                            { created_at: desc },
                        ]
                    ) {
                        ${queryArticlesReturn}
                    }

                    ${topicsGql}
                }

            `)
            .catch(err => {
                console.warn(err)
                return { latest: [], featured: [], topicsObj: {} }
            })

        const featuredIds = featured
            .map(obj => obj.id)

        const { recommended, exclusive } = await graphqlClient
            .request(gql`
                query {
                    recommended: articles (
                        where: {
                            _and: [
                                { recommended_roles: { _is_null: false } },
                                ${featuredIds.map(int => `{ id: { _neq: ${int} } }`)}
                            ]
                        },
                        order_by: [
                            { recommended_roles: asc },
                            { created_at: desc },
                        ]
                        limit: 2,
                    ) {
                        ${queryArticlesReturn}
                    }

                    exclusive: articles (
                        where: {
                            _and: [
                                { source_id: { _is_null: true } },
                                { thumbnail: { _is_null: false } },
                                ${featuredIds.map(int => `{ id: { _neq: ${int} } }`)}
                            ]
                        },
                        limit: 12,
                        order_by: { created_at: desc }
                    ) {
                        ${queryArticlesReturn}
                    }
                }
            `)
            .catch(err => {
                console.warn(err)
                return { recommended: [], exclusive: [] }
            })

        const queryTopics = topics
            .filter(obj => obj.slug
                && (topicsObj[camelCase(obj.slug)] || []).length)
            .map((obj) => {
                const { title, slug } = obj

                obj.articles = [{
                    title,
                    slug
                }].concat(topicsObj[camelCase(obj.slug)] || [])

                return obj
            })

        if (featured.length > 1)
            setFeatured(featured)

        if (recommended)
            setRecommended(recommended)

        setLatest(latest)

        if (exclusive)
            setExclusive(exclusive)

        setTopicArticles(queryTopics)

        history.replace(`${location.pathname}${location.search}`,
            Object.assign(history.location.state || {}, {
                featured: featured || null,
                recommended: recommended || null,
                latest: latest || null,
                exclusive: exclusive || null,
                topicArticles: queryTopics || null
            }))
    }, [])

    const isFeatured = Boolean(featured && featured.length)

    const isRecommended = Boolean(recommended && recommended.length)

    const isLatest = Boolean(latest && latest.length)

    const isExclusive = Boolean(exclusive && exclusive.length)

    const isTopics = Boolean(topicArticles && topicArticles.length)

    const isLoading = !isFeatured
        && !isRecommended
        && !isLatest
        && !isExclusive
        && !isTopics

    const renderTopicCard = (topic) => (topic || {}).slug
        && (topic || {}).title
        && (
            <Link
                className="dashboard-hub-landing-topic-card card text-decoration-hover"
                to={`${searchPathname}?topic=${topic.slug}`}
            >
                <span>View all articles in {topic.title}</span>
                <div className="pad-t">
                    <div
                        className="ratio-square round"
                        style={{ width: 30 }}
                    >
                        <svg width="24" height="24">
                            <use xlinkHref="#icon-chevron-right" />
                        </svg>
                    </div>
                </div>
            </Link>)

    return (
        <div className="dashboard-hub-landing card card-offscreen-x-lg-down pad-3-t">
            {isLoading
                && (
                    <div className="dashboard-hub-pending-results display-flex flex-align-items-center flex-justify-content-center">
                        <LoadingIndicator />
                    </div>)}
            {isFeatured
                && (
                    <section className="dashboard-hub-landing-featured">
                        <div className="row-condensed text-screen-reader">
                            <div className="col">
                                <h2>Featured</h2>
                            </div>
                        </div>
                        <ul className="row-condensed margin-2-b-neg">
                            {featured
                                .map((obj, idx) => (
                                    <li
                                        className="col col-12-md-down margin-b"
                                        key={`dashboard-hub-landing-featured-${idx}`}
                                    >
                                        <ArticleCard {...obj} />
                                    </li>
                                ))}
                        </ul>
                    </section>)}
            {!isLoading
                    && (
                        <div className="display-none-lg-down">
                            <DashboardHubTopicsCard
                                topics={topics}
                                searchPathname={searchPathname}
                            />
                        </div>)}
            {isRecommended
                && (
                    <section className="dashboard-hub-landing-recommended">
                        <div
                            className="row-condensed margin-1-b margin-2-b-md-up"
                            style={{ paddingRight: 76 }}
                        >
                            <div className="col">
                                <h2 className="heading-5">
                                    Recommended
                                </h2>
                            </div>
                        </div>
                        <Swiper
                            wrapperClassName="row-condensed"
                            slideClassName="col-10 col-4-md col-3-lg-up"
                            pagination={{
                                type: 'fraction'
                            }}
                        >
                            {recommended
                                .map((obj, idx) => (
                                    <ArticleCard
                                        key={`dashboard-hub-landing-recommended-${idx}`}
                                        {...obj}
                                    />
                                ))}
                        </Swiper>
                    </section>)}
            {isLatest
                && (
                    <section className="dashboard-hub-landing-latest">
                        <div
                            className="row-condensed flex-justify-content-center margin-1-b margin-2-b-md-up"
                            style={{ paddingRight: 76 }}
                        >
                            <div className="col">
                                <h2 className="heading-5">
                                    Latest
                                </h2>
                            </div>
                            <div className="col-auto">
                                <Link
                                    className="body-4 display-block text-decoration-hover"
                                    to={`${searchPathname}?duration=all`}
                                    style={{ color: getCssVar('--teal') }}
                                >
                                    <span>View all</span>
                                </Link>
                            </div>
                        </div>
                        <Swiper
                            wrapperClassName="row-condensed"
                            slideClassName="col-10 col-4-md col-3-lg-up"
                            pagination={{
                                type: 'fraction'
                            }}
                            breakpoints={{
                                768: {
                                    slidesPerView: 3,
                                    autoHeight: true
                                },
                                1024: {
                                    slidesPerView: 4,
                                    autoHeight: true
                                }
                            }}
                        >
                            {latest
                                .map((obj, idx) => (
                                    <ArticleCard
                                        key={`dashboard-hub-landing-latest-${idx}`}
                                        {...obj}
                                    />
                                ))}
                        </Swiper>
                    </section>)}
            {isExclusive
                && (
                    <section className="dashboard-hub-landing-exclusive">
                        <div
                            className="row-condensed flex-justify-content-center margin-1-b margin-2-b-md-up"
                            style={{ paddingRight: 76 }}
                        >
                            <div className="col">
                                <h2 className="heading-5 display-flex">
                                    <span className="display-inline-block">
                                        Catapult exclusive
                                    </span>
                                    <span
                                        className="display-inline-flex flex-none"
                                        style={{ marginLeft: 6 }}
                                    >
                                        <span
                                            className="ratio-square round flex-justify-content-center"
                                            style={{
                                                width: 22,
                                                height: 22,
                                                background: getCssVar('--aqua'),
                                                color: getCssVar('--navy'),
                                                marginTop: 0
                                            }}
                                        >
                                            <svg
                                                width="11"
                                                style={{ marginLeft: -1 }}
                                            >
                                                <use xlinkHref="#logo-catapult-icon" />
                                            </svg>
                                        </span>
                                    </span>
                                </h2>
                            </div>
                            <div className="col-auto">
                                <Link
                                    className="body-4 display-block text-decoration-hover"
                                    to={`${searchPathname}?source=catapult`}
                                    style={{ color: getCssVar('--teal') }}
                                >
                                    <span>View all</span>
                                </Link>
                            </div>
                        </div>
                        <Swiper
                            wrapperClassName="row-condensed"
                            slideClassName="col-10 col-6-md-up"
                            pagination={{
                                type: 'fraction'
                            }}
                        >
                            {exclusive
                                .map((obj, idx) => (
                                    <ArticleCard
                                        key={`dashboard-hub-landing-exclusive-${idx}`}
                                        {...obj}
                                    />
                                ))}
                        </Swiper>
                    </section>)}
            {isTopics
                && <hr className="margin-0-t" />}
            {isTopics
                && topicArticles
                    .map((obj, idx) => (
                        <section
                            className={`dashboard-hub-landing-topic dashboard-hub-landing-topic-${idx}`}
                            key={`dashboard-hub-landing-topic-${idx}`}
                        >
                            <div
                                className="row-condensed flex-justify-content-center margin-1-b margin-2-b-md-up"
                                style={{ paddingRight: 76 }}
                            >
                                <div className="col">
                                    <h2 className="heading-5">
                                        {obj.title}
                                    </h2>
                                </div>
                                <div className="col-auto">
                                    <Link
                                        className="body-4 display-block text-decoration-hover"
                                        to={`${searchPathname}?topic=${obj.slug}`}
                                        style={{ color: getCssVar('--teal') }}
                                    >
                                        <span>View all</span>
                                    </Link>
                                </div>
                            </div>
                            <div
                                className="row-condensed flex-nowrap"
                                style={{ position: 'static' }}
                            >
                                <div
                                    className="dashboard-hub-landing-topic-card-wrapper col-3 col-2-xl-up display-none-md-down"
                                >
                                    {renderTopicCard(obj)}
                                </div>
                                <div
                                    className="col-12 col-9-md-up col-10-xl-up"
                                    style={{ position: 'static' }}
                                >
                                    <Swiper
                                        wrapperClassName="row-condensed"
                                        slideClassName="col-7 col-5-sm col-4-md-up col-3-xl-up"
                                        pagination={{
                                            type: 'fraction'
                                        }}
                                        breakpoints={{
                                            768: {
                                                autoHeight: true,
                                                slidesPerView: 3
                                            },
                                            1280: {
                                                autoHeight: true,
                                                slidesPerView: 4
                                            }
                                        }}
                                    >
                                        {obj.articles
                                            .map((_obj, _idx) => !_idx
                                                ? (window.innerWidth < 768
                                                    ? (
                                                        <div
                                                            key={`dashboard-hub-landing-topic-${idx}-${_idx}`}
                                                            className="display-flex flex-grow"
                                                        >
                                                            {renderTopicCard(obj)}
                                                        </div>)
                                                    : null)
                                                : (
                                                    <ArticleCard
                                                        key={`dashboard-hub-landing-topic-${idx}-${_idx}`}
                                                        {..._obj}
                                                    />)
                                            )}
                                    </Swiper>
                                </div>
                            </div>
                        </section>
                    ))}
        </div>
    )
}
